import type { RecipeReadCollectionType, CreateRecipeType, RecipeReadType } from '@readme/api-contracts/schemas/recipe';
import type { TutorialSchema } from '@readme/backend/models/tutorial/types';

type LegacyRecipeBase = Pick<
  TutorialSchema,
  // Grabbing only the properties the frontend cares about
  'backgroundColor' | 'description' | 'emoji' | 'published' | 'response' | 'slug' | 'stepCount' | 'steps' | 'title'
>;

type LegacyPageReference = TutorialSchema['referenceDisplay'][number];
type LegacySnippetEndpointPage = TutorialSchema['snippet']['endpoint']['page'];

export type LegacyRecipeType = LegacyRecipeBase & {
  // Legacy recipes use ObjectId/Page docs, SuperHub-normalized payloads use slugs.
  referenceDisplay: (LegacyPageReference | string)[];
  snippet: Omit<TutorialSchema['snippet'], 'endpoint'> & {
    endpoint: Omit<TutorialSchema['snippet']['endpoint'], 'page'> & {
      page: LegacySnippetEndpointPage | string;
    };
  };
};

export interface ApiV2RecipeEndpointGroup {
  category: string;
  children: {
    operationId: string;
  }[];
  title: string;
}

export interface ApiV2RecipeEndpointsResponse {
  endpoints: ApiV2RecipeEndpointGroup[];
}

interface LegacyRecipeEndpointsResponse {
  endpoints: {
    categoryId: string;
    children: {
      _id: string;
    }[];
    title: string;
  }[];
}

/**
 * Converts a recipe representation from the APIv2 format to the legacy format.
 */
export function apiNextRecipeToLegacy(data: RecipeReadType['data']): LegacyRecipeType {
  const { appearance, content, ...properties } = data;

  return {
    ...properties,
    description: properties.description as string,
    backgroundColor: appearance.background_color as string,
    emoji: (appearance.emoji as string) || null,
    snippet: {
      codeOptions: content.snippet.code_options.map(option => ({
        highlightedSyntax: option.highlighted_syntax || '',
        code: option.code || '',
        language: option.language || '',
        name: option.name || '',
      })),
      // We're intentionally not saving the selected endpoint in APIv2, so stub this out
      endpoint: {
        method: '',
        page: '',
        slug: '',
        title: '',
      },
    },
    steps: content.steps.map(step => ({
      title: step.title,
      body: step.body || '',
      lineNumbers: step.line_numbers,
    })),
    stepCount: content.steps.length,
    response: content.response || '',
    published: data.privacy.view === 'public',
    referenceDisplay: (data.connections?.references || []).map(ref => {
      // Extract the slug from the URI (e.g. `/versions/1.0/reference/get-user` → `get-user`)
      const segments = ref.uri.split('/');
      return segments[segments.length - 1];
    }),
  };
}

/**
 * Converts a recipe representation from the legacy format to the APIv2 format.
 */
export function legacyRecipeToApiNext(data: LegacyRecipeType, branch?: string): CreateRecipeType {
  const result: CreateRecipeType = {
    appearance: {
      background_color: data.backgroundColor,
      emoji: data.emoji,
    },
    connections: {
      references: data.referenceDisplay?.map(reference => ({
        uri: `/versions/${branch}/reference/${reference}`,
      })),
    },
    content: {
      snippet: {
        code_options: data.snippet?.codeOptions?.map(option => ({
          code: option.code,
          language: option.language,
          name: option.name,
          highlighted_syntax: option.highlightedSyntax,
        })),
      },
      steps: data.steps?.map(step => ({
        title: step.title,
        body: step.body,
        line_numbers: step.lineNumbers,
      })),
      response: data.response,
    },
    description: data.description,
    privacy: {
      view: data.published ? 'public' : 'anyone_with_link',
    },
    title: data.title,
  };

  return result;
}

/**
 * Normalizes the legacy endpoints response to match the shape expected by the frontend components.
 * Legacy endpoints return `_id` and `categoryId`, but the components expect `operationId` and `category`.
 */
export function normalizeLegacyEndpointsResponse(
  response: LegacyRecipeEndpointsResponse,
): ApiV2RecipeEndpointsResponse {
  return {
    endpoints: response.endpoints.map(group => ({
      ...group,
      category: group.categoryId,
      children: group.children.map(child => ({
        ...child,
        operationId: child._id,
      })),
    })),
  };
}

/**
 * Normalizes the response for the `deleteTutorial` API action from the APIv2 response payload to the legacy response format.
 */
export function normalizeDeleteTutorialResponse() {
  return { success: true };
}

/**
 * Normalizes the response for the `getTutorial` API action from the APIv2 response payload to the legacy response format.
 */
export function normalizeGetTutorialResponse(response: RecipeReadType) {
  return {
    tutorial: apiNextRecipeToLegacy(response.data),
  };
}

/**
 * Normalizes the response for the `getTutorials` API action from the APIv2 response payload to the legacy response format.
 */
export function normalizeGetTutorialsResponse(response: RecipeReadCollectionType) {
  const tutorials = response.data.map(apiNextRecipeToLegacy);

  // For APIv2 recipes the hero is the first recipe in the array
  return {
    hero: tutorials[0],
    tutorials: tutorials.map((tutorial, i) => ({
      ...tutorial,
      hero: i === 0,
    })),
    tutorialOnboarding: {
      published: !!tutorials.length,
      endpoints: true, // SuperHub doesn't use the legacy endpoint onboarding flow
    },
  };
}

/**
 * Normalizes the response for the `createTutorial` and `updateTutorial` API actions from the legacy request body payload
 * to the APIv2 request body payload.
 */
export function normalizeUpsertTutorialRequestBody(data, branch?: string): CreateRecipeType {
  return legacyRecipeToApiNext(data, branch);
}

/**
 * Normalizes the response for the `createTutorial` and `updateTutorial` API actions from the APIv2 response payload to
 * the legacy response format.
 */
export function normalizeUpsertTutorialResponse(response: RecipeReadType) {
  return {
    tutorial: apiNextRecipeToLegacy(response.data),
    success: true,
    // this flag is only used to open the modal when navigating to a tutorial, so setting it to true equates
    // to "not a GET request"
    tutorialPublishToggled: true,
  };
}
